<template>
  <div class="mt-6 scroll-area" >
    <vs-table :data="emissions" v-if="valid">
      <template slot="header">
        <h5 class="mt-6">{{ $t('Decarbonization.EnergyScenarios.EnergySimulation') }}</h5>
        <vs-button type="border" class="mb-2" icon-pack="feather" icon="icon-rotate-cw" @click="calculation">{{ $t('Refresh') }}</vs-button>
      </template>
      
      <template slot="thead">
        <vs-th>{{$t('Decarbonization.EnergyScenarios.Year')}}</vs-th>
        <vs-th v-for="year in years" :key="'header' + year" class="year">
          <span>{{ year }}</span>
        </vs-th>
      </template>

      <template>
        <tbody id="baseInfoHeader">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.EnergySimulation') }}</vs-td>
            <vs-td v-for="year in years" :key="'BaseInfoHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-if="requiresComplexityParameter">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.Tonnage') }}</vs-td>
            <vs-td v-for="year in years" :key="'BaseInfoHeaderTonnageValue' + year" class="text-right">
              <span>{{ getTonnage(year) | toFriendlyNumber }}</span>
            </vs-td>
          </vs-tr>
          <vs-tr v-if="requiresComplexityParameter">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.ComplexityFactor') }}</vs-td>
            <vs-td v-for="year in years" :key="'BaseInfoHeaderComplexityFactor' + year" class="text-right">
              <span>{{ getFactor(year) | toFriendlyNumber  }}</span>
            </vs-td>
          </vs-tr>
          <vs-tr>
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.EnergyConsumption') }}</vs-td>
            <vs-td v-for="year in years" :key="'BaseInfoHeaderTotalEnergyValue' + year" class="text-right">
              <span>{{ getEnergy(year) | toFriendlyNumber }}</span>
            </vs-td>
          </vs-tr>
        </tbody>

        <tbody id="energySupplyRateHeader" v-if="hasCalculatedEmissions">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyTypeRatio.EnergySource') }}</vs-td>
            <vs-td v-for="year in years" :key="'EnergySupplyRateHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-for="source in energySourceEnumNumericalValues" :key="'EnergySupplyRateRow' + source">
            <vs-td>{{ getEnergySourceName(source) }}</vs-td>
            <vs-td v-for="year in years" :key="'EnergySupplyRateValue' + source + year" class="text-right">
              {{ getEnergySupplyRate(year, source) | toFriendlyNumber }}
            </vs-td>
          </vs-tr>
        </tbody>

        <tbody id="energyDemandHeader" v-if="hasCalculatedEmissions">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.EnergyDemand') }}</vs-td>
            <vs-td v-for="year in years" :key="'EnergyDemandHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-for="source in energySourceEnumNumericalValues" :key="'EnergyDemandRow' + source">
            <vs-td>{{ getEnergySourceName(source) }}</vs-td>
            <vs-td v-for="year in years" :key="'EnergyDemandValue' + source + year" class="text-right">
              {{ getEnergyDemand(year, source) | toFriendlyNumber }}
            </vs-td>
          </vs-tr>
        </tbody>

        <tbody id="kWhEquivalentHeader" v-if="hasCalculatedEmissions">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.KWhEquivalent') }}</vs-td>
            <vs-td v-for="year in years" :key="'KWhEquivalentHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-for="source in energySourceEnumNumericalValues" :key="'KWhEquivalentRow' + source">
            <vs-td>{{ getEnergySourceName(source) }}</vs-td>
            <vs-td v-for="year in years" :key="'KWhEquivalentValue' + source + year" class="text-right">
              {{ getKWhEquivalent(year, source) | toFriendlyNumber }}
            </vs-td>
          </vs-tr>
        </tbody>

        <tbody id="mwEquivalentHeader" v-if="hasCalculatedEmissions">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.MwEquivalent') }}</vs-td>
            <vs-td v-for="year in years" :key="'MwEquivalentHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-for="source in energySourceEnumNumericalValues" :key="'MwEquivalentRow' + source">
            <vs-td>{{ getEnergySourceName(source) }}</vs-td>
            <vs-td v-for="year in years" :key="'MwEquivalentValue' + source + year" class="text-right">
              {{ getMwEquivalent(year, source) | toFriendlyNumber }}
            </vs-td>
          </vs-tr>
        </tbody>

        <tbody id="litersEquivalentHeader" v-if="hasCalculatedEmissions">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.LitersEquivalent') }}</vs-td>
            <vs-td v-for="year in years" :key="'LitersEquivalentHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-for="source in energySourceEnumNumericalValues" :key="'LitersEquivalentRow' + source">
            <vs-td>{{ getEnergySourceName(source) }}</vs-td>
            <vs-td v-for="year in years" :key="'LitersEquivalentValue' + source + year" class="text-right">
              {{ getLitersEquivalent(year, source) | toFriendlyNumber }}
            </vs-td>
          </vs-tr>
        </tbody>
        
        <tbody id="cO2FactorHeader" v-if="hasCalculatedEmissions">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.CO2Factor') }}</vs-td>
            <vs-td v-for="year in years" :key="'CO2FactorHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-for="source in energySourceEnumNumericalValues" :key="'CO2FactorRow' + source">
            <vs-td>{{ getEnergySourceName(source) }}</vs-td>
            <vs-td v-for="year in years" :key="'CO2FactorValue' + source + year" class="text-right">
              {{ getCO2Factor(year, source) | toFriendlyNumber }}
            </vs-td>
          </vs-tr>
        </tbody>

        <tbody id="cO2EquivalentHeader" v-if="hasCalculatedEmissions">
          <vs-tr :state="'header'">
            <vs-td>{{ $t('Decarbonization.EnergyScenarios.CO2Equivalent') }}</vs-td>
            <vs-td v-for="year in years" :key="'CO2EquivalentHeader' + year"></vs-td>
          </vs-tr>
          <vs-tr v-for="source in energySourceEnumNumericalValues" :key="'CO2EquivalentRow' + source">
            <vs-td>{{ getEnergySourceName(source) }}</vs-td>
            <vs-td v-for="year in years" :key="'CO2EquivalentValue' + source + year" class="text-right">
              {{ getCO2Equivalent(year, source) | toFriendlyNumber }}
            </vs-td>
          </vs-tr>
        </tbody>
        
      </template>

    </vs-table>
    
  </div>
</template>

<script>

import CalculationMethodEnum from '@/app/bsg/decarbonization/enums/CalculationMethod.js'

export default {
  props: {
    complexityParameter: [Number, String],
    lomScenarioTypeId: String,
    lomVersionId: String,
    lomEntityId: String,
    calculationMethod: [Number, String],
    lomParameterId: Number,
    createNew: Boolean,
    energySupplyTemplateId: [Number, String],
    gridEfficiencyTemplateId: [Number, String],
    externalLomId: [Number, String],
    yearlyEnergyDemand: [],
    yearlyEmissions: []
  },
  data() {
    return {
      energyDemand: [],
      emissions: []
    };
  },
  created() {
    if (!this.createNew) {
      this.energyDemand = this.yearlyEnergyDemand;
      this.emissions = this.yearlyEmissions;
    }
  },

  computed: {
    valid() {
      const notNull = (!this.requiresComplexityParameter || this.complexityParameter !== null) && this.calculationMethod !== null && this.energySupplyTemplateId !== null && this.gridEfficiencyTemplateId !== null;
      const notEmpty = (!this.requiresComplexityParameter || this.complexityParameter !== '') && this.calculationMethod !== '' && this.energySupplyTemplateId !== '' && this.gridEfficiencyTemplateId !== '';
      const validId = (!this.requiresComplexityParameter || this.complexityParameter > 0) && this.calculationMethod >= 0;
      return notNull && notEmpty && validId;
    },
    requiresComplexityParameter() {
      return this.calculationMethod !== CalculationMethodEnum.ManualImportOfTotalEnergy;
    },
    years () {
      return this.energyDemand.map(x => x.year).sort();
    },
    energySourceEnumNumericalValues() {
      if (this.hasCalculatedEmissions) {
        const allSources =  this.emissions.map(x => x.energySourceEmissions.map(x => x.energySource)).flat();
        // return distinct values
        return [...new Set(allSources)];
      }
      return []; 
    },
    hasCalculatedEmissions() {
      return this.emissions.length > 0;
    }
  },

  watch: {
    // There must be a better way to do this
    complexityParameter() {
      if (this.valid) this.calculation();
    },
    calculationMethod() {
      if (this.valid) this.calculation();
    },
    gridEfficiencyTemplateId() {
      if (this.valid) this.calculation();
    },
    energySupplyTemplateId() {
      if (this.valid) this.calculation();
    }
  },

  methods: {
    calculation() {
      this.$vs.loading();
      if (!this.lomParameterId || this.lomParameterId === 0) {
        const params = {
          externalLomId: this.externalLomId,
          entityId: this.lomEntityId,
          scenarioTypeId: this.lomScenarioTypeId,
          versionId: this.lomVersionId,
          calculationMethod: this.calculationMethod,
          complexityParameterId: this.complexityParameter,
          energySupplyTemplateId: this.energySupplyTemplateId,
          gridEfficiencyTemplateId: this.gridEfficiencyTemplateId
        };
        this.$cqrsPost("Cashflow/Decarbonization/EnergyScenarios/simulateCalculation/", params)
          .then((response) => {
            const model = response.data.model;
            this.energyDemand = model.yearlyEnergyDemand;
            this.emissions = model.yearlyEmissions;
          }).finally(() => this.$vs.loading.close());
      }
      else {
        const calculateParams = {
          lomParameterId: this.lomParameterId,
          calculationMethod: this.calculationMethod,
          complexityParameterId: this.complexityParameter,
          energySupplyTemplateId: this.energySupplyTemplateId,
          gridEfficiencyTemplateId: this.gridEfficiencyTemplateId
        };
        this.$cqrsPost("Cashflow/Decarbonization/EnergyScenarios/calculate/", calculateParams)
          .then((response) => {
            const model = response.data.model;
            this.energyDemand = model.yearlyEnergyDemand;
            this.emissions = model.yearlyEmissions;
          }).finally(() => this.$vs.loading.close());
      }
    },
    getEnergySourceName(source) {
      return this.$t("Decarbonization.EnergySourcesEnum." + source);
    },
    getTonnage(year) {
      return this.energyDemand.find(x => x.year === year).tonnage;
    },
    getFactor(year) {
      return this.energyDemand.find(x => x.year === year).factor;
    },
    getEnergy(year) {
      return this.energyDemand.find(x => x.year === year).totalEnergy;
    },    
    getEnergySourceEmission(year, source) {
      return this.emissions.find(x => x.year === year).energySourceEmissions.find(x => x.energySource === source);
    },
    getEnergySupplyRate(year, source) {
      const result = this.getEnergySourceEmission(year, source);
      return result !== undefined ? result.energySupplyRate : 0;
    },
    getEnergyDemand(year, source) {
      const result = this.getEnergySourceEmission(year, source);
      return result !== undefined ? result.energyDemand : 0;
    },
    getKWhEquivalent(year, source) {
      const result = this.getEnergySourceEmission(year, source);
      return result !== undefined ? result.kWhEquivalent : 0;
    },
    getMwEquivalent(year, source) {
      const result = this.getEnergySourceEmission(year, source);
      return result !== undefined ? result.mwEquivalent : 0;
    },
    getLitersEquivalent(year, source) {
      const result = this.getEnergySourceEmission(year, source);
      return result !== undefined ? result.litersEquivalent : 0;
    },
    getCO2Factor(year, source) {
      const result = this.getEnergySourceEmission(year, source);
      return result !== undefined ? result.cO2Factor : 0;
    },
    getCO2Equivalent(year, source) {
      const result = this.getEnergySourceEmission(year, source);
      return result !== undefined ? result.cO2Equivalent : 0;
    }
  }
};
</script>
<style>
  .tr-table-state-header {
    background-color: #ededed;
    font-weight: bold;
  }

  .year {
    align-content: center;
  }

  .scroll-area {
    overflow-x: auto;
  }
</style>